<script>
  import { initClient, operationStore, query } from '@urql/svelte';
  import { arrayToLookupTable } from './utils.js';

  import FilterGroup from './FilterGroup.svelte';
  import Map from './Map.svelte';
  import MapMarker from './MapMarker.svelte';
  import ProjectList from './ProjectList.svelte';
  import ProjectListItem from './ProjectListItem.svelte';
  import CardsGrid from './CardsGrid.svelte';
  import CardsGridItem from './CardsGridItem.svelte';

  // Init URQL client
  initClient({
    url: '/api',
  });

  const projectsQuery = operationStore(`
    query ($q: String) {
      projects: entries(section: "projects", limit: null, search: $q, orderBy: "completionYear DESC, title ASC") {
        title
        id
        url
        ... on projects_project_Entry {
          typologies {
            id
          }

          categories {
            id
          }
          neighborhood {
            title
            id
          }
          designer (limit: 1) {
            title
            id
          }
          year: completionYear
          lat
          lng
          image: featuredImage {
            srcset: url @imagerSrcset (handle:"card")
            transform: imagerTransform(transform: "card") {
              width
              height
              url
            }
          }
          imageAlt: featuredImageAlt
        }
      }
    }
    `,
    { q: searchQuery },
    { pause: true }
  );

  query(projectsQuery);

  // Init Google Maps
  let mapReady;

  if (process.browser) {
    window.initMap = () => {
      mapReady = true;
    };
  }

  export let typologies = [];
  export let categories = [];
  export let neighborhoods = [];
  export let designers = [];
  export let years = [];
  export let projects = [];

  const typologyTable = arrayToLookupTable(typologies);

  let queryParams = new URLSearchParams(window.location.search);

  let searchQuery = queryParams.get('q') ?? '';
  let selectedTypologies = getQueryParam('t');
  let selectedCategories = getQueryParam('c');
  let selectedNeighborhoods = getQueryParam('n');
  let selectedDesigners = getQueryParam('d');
  // Year needs to be an integer value.
  // Remove spaces, convert to integer, and filter out falsy values like NaN
  let selectedYears = getQueryParam('y').map(n => parseInt(n.replace(/\s+/g, ''), 10)).filter(Boolean);

  let filteredProjects = projects;

  let activeCard = 0;

  function getQueryParam(key) {
    let params = queryParams.get(key);
    if (typeof params === 'string') {
      return params.split(',');
    }
    return [];
  }

  function matchAnyIds(categories, matchArray) {
    if (categories.length > 0 && matchArray.length > 0) {
      return categories.some(cat => matchArray.includes(cat.id));
    }
    return false;
  }

  function updateQueryParams(key, values) {
    if (values.length) {
      queryParams.set(key, values)
    } else {
      queryParams.delete(key);
    }
  }

  function updateUrl() {
    updateQueryParams('t', selectedTypologies);
    updateQueryParams('c', selectedCategories);
    updateQueryParams('n', selectedNeighborhoods);
    updateQueryParams('d', selectedDesigners);
    updateQueryParams('y', selectedYears);

    let baseUrl = window.location.pathname;
    history.replaceState({}, '', `${baseUrl}${queryParams.toString().length > 0 ? `?${queryParams}`: ""}` );
  }

  $: {
    filteredProjects = projects;
    if (selectedTypologies.length > 0) {
      filteredProjects = filteredProjects.filter(project => matchAnyIds(project.typologies, selectedTypologies));
    }
    if (selectedCategories.length > 0) {
      filteredProjects = filteredProjects.filter(project => matchAnyIds(project.categories, selectedCategories));
    }
    if (selectedNeighborhoods.length > 0) {
      filteredProjects = filteredProjects.filter(project => matchAnyIds(project.neighborhood, selectedNeighborhoods));
    }
    if (selectedDesigners.length > 0) {
      filteredProjects = filteredProjects.filter(project => matchAnyIds(project.designer, selectedDesigners));
    }
    if (selectedYears.length > 0) {
      filteredProjects = filteredProjects.filter(project => selectedYears.includes(project.year));
    }
    updateUrl();
  }

  function searchProjects() {
    updateQueryParams('q', searchQuery);
    updateUrl();

    $projectsQuery.variables = { q: searchQuery };
    $projectsQuery.context = { pause: false };
  }

  $: if ($projectsQuery.data) {
    projects = $projectsQuery.data.projects;
  }

  function resetSearch() {
    activeCard = 0;
    searchQuery = '';
    selectedTypologies = [];
    selectedCategories = [];
    selectedNeighborhoods = [];
    selectedDesigners = [];
    selectedYears = [];
    searchProjects();
  }

</script>

<div class="main-sidebar">
  <nav>
    <div class="nav nav-tabs view-nav" id="nav-tab" role="tablist">
      <p class="nav-link view-nav-link view-nav-label">View by</p>
      <button class="nav-link view-nav-link active" id="nav-map-tab" data-bs-toggle="tab" data-bs-target="#project-map" type="button" role="tab" aria-controls="project-map" aria-selected="true">
        <img class="view-nav-icon" src="/theme/img/icon-map-marker.svg" width="20" height="23" alt="" >
        Map
      </button>
      <button class="nav-link view-nav-link" id="nav-list-tab" data-bs-toggle="tab" data-bs-target="#project-list" type="button" role="tab" aria-controls="project-list" aria-selected="false">
        <img class="view-nav-icon" src="/theme/img/icon-list.svg" width="25" height="15" alt="" >
        List
      </button>
      <button class="nav-link view-nav-link" id="nav-images-tab" data-bs-toggle="tab" data-bs-target="#project-cards" type="button" role="tab" aria-controls="project-cards" aria-selected="false">
        <img class="view-nav-icon" src="/theme/img/icon-grid.svg" width="20" height="20" alt="" >
        Images
      </button>
    </div>
  </nav>

  <form class="filter" action="" id="filter" on:submit|preventDefault="{searchProjects}">
    <div class="search-filter" role="search">
      <label class="search-filter-label" for="search-input">
        Search
        {#if $projectsQuery.fetching}
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        {/if}
      </label>
      <input class="form-control search-filter-input" id="search-input" autocomplete="off" type="search" name="q" placeholder="Type here" bind:value="{searchQuery}" on:search|preventDefault="{searchProjects}">
    </div>

    <div class="filter-mobile-collapse collapse" id="filter-mobile-collapse">

      <fieldset class="filter-group">
        <legend class="filter-heading">
          <button class="filter-toggle" type="button" data-bs-toggle="collapse" data-bs-target="#typology-filter" aria-expanded="false" aria-controls="typology-filter">
            Sort by Typology
          </button>
        </legend>

        <div class="filter-collapse collapse typology-filter" class:has-checked={selectedTypologies.length > 0} id="typology-filter">
          <div class="typology-filter-list">
            {#each typologies as item}
              <input class="typology-filter-input" type="checkbox" bind:group={selectedTypologies} value={item.id} id="typology-input-{item.id}" style="--typology-color:{item.color}">
              <label class="typology-filter-label" for="typology-input-{item.id}">
                {item.title}
              </label>
            {/each}
          </div>
        </div>
      </fieldset>

      <FilterGroup label="Category" items={categories} bind:selected={selectedCategories}/>
      <FilterGroup label="Neighborhood" items={neighborhoods} bind:selected={selectedNeighborhoods}/>
      <FilterGroup label="Designer" items={designers} bind:selected={selectedDesigners}/>
      <FilterGroup label="Year" items={years.map(y => ({ title: y, id: y }))} bind:selected={selectedYears}/>

    </div>

    <button class="filter-toggle filter-toggle-mobile collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#filter-mobile-collapse" aria-expanded="false" aria-controls="filter-mobile-collapse">
      Sort by
    </button>

  </form>
</div>

<div class="main-content">
  <div class="tab-content" id="nav-tabContent" style:display={filteredProjects.length > 0 ? 'block': 'none'}>
    <div class="tab-pane fade show active" id="project-map" role="tabpanel" aria-labelledby="nav-map-tab">

      {#if mapReady}
        <Map>
          {#each filteredProjects as project (project.id)}
            <MapMarker {project} {typologyTable} />
          {/each}
        </Map>
      {/if}

    </div>

    <div class="tab-pane fade" id="project-list" role="tabpanel" aria-labelledby="nav-list-tab">
      <ProjectList>
        {#each filteredProjects as project (project.id)}
          <ProjectListItem {project}/>
        {/each}
      </ProjectList>
    </div>

    <div class="tab-pane fade" id="project-cards" role="tabpanel" aria-labelledby="nav-images-tab">
      <CardsGrid bind:activeCard>
        {#each filteredProjects as project, i (project.id)}
          <CardsGridItem {project} {typologyTable} bind:activeCard/>
        {/each}
      </CardsGrid>
    </div>

  </div>
  {#if filteredProjects.length === 0}
    <p class="display">
      There are no search results.<br>
      Would you like to see <a href="/" on:click|preventDefault={resetSearch}>all projects</a>?
    </p>
  {/if}
</div>
